import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useLanguage } from '../../i18n/LanguageContext';
import { Routine, Exercise, RoutineDay } from '../../types';
import { EditDayDialog } from './EditDayDialog';
import { commonDialogStyles } from '../common/dialogStyles';

interface EditRoutineDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (routine: Routine) => void;
  routine: Routine;
  exercises: Exercise[];
  onCreateExercise?: (name: string) => Promise<Exercise | null>;
  setExercises?: (exercises: Exercise[]) => void;
}

export const EditRoutineDialog: React.FC<EditRoutineDialogProps> = ({
  open,
  onClose,
  onSave,
  routine,
  exercises,
  onCreateExercise,
  setExercises
}) => {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [days, setDays] = useState<RoutineDay[]>([]);
  const [editDayDialogOpen, setEditDayDialogOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<RoutineDay | null>(null);
  const [expandedDay, setExpandedDay] = useState<{ [key: string]: boolean }>({});

  // Initialize form with routine data
  useEffect(() => {
    if (routine) {
      setName(routine.name);
      setDescription(routine.description || '');
      setIsActive(routine.is_active);
      setDays([...routine.days]);
    }
  }, [routine]);

  const handleClose = () => {
    setExpandedDay({});
    onClose();
  };

  const handleSave = () => {
    const updatedRoutine: Routine = {
      ...routine,
      name,
      description,
      is_active: isActive,
      days: days.map((day, index) => ({
        ...day,
        day_number: index + 1
      }))
    };
    onSave(updatedRoutine);
    handleClose();
  };

  const addNewDay = () => {
    const newDay: RoutineDay = {
      day_number: days.length + 1,
      day_name: '',
      notes: '',
      exercises: []
    };
    setSelectedDay(newDay);
    setEditDayDialogOpen(true);
  };

  const editDay = (day: RoutineDay) => {
    setSelectedDay({ ...day }); // Create a copy to avoid direct mutation
    setEditDayDialogOpen(true);
  };

  const deleteDay = (dayToDelete: RoutineDay) => {
    setDays(days.filter(day => day !== dayToDelete));
  };

  const handleDaySave = (updatedDay: RoutineDay) => {
    if (selectedDay && days.some(day => day.id === selectedDay.id || day === selectedDay)) {
      // Editing existing day
      setDays(days.map(day => 
        (day.id === selectedDay.id || day === selectedDay) ? updatedDay : day
      ));
    } else {
      // Adding new day
      setDays([...days, updatedDay]);
    }
    setEditDayDialogOpen(false);
    setSelectedDay(null);
  };

  const handleDayCancel = () => {
    setEditDayDialogOpen(false);
    setSelectedDay(null);
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedDay(prev => ({
      ...prev,
      [panel]: isExpanded
    }));
  };

  const isAccordionExpanded = (index: number) => {
    const panelKey = `day-${index}`;
    return expandedDay[panelKey] !== undefined ? expandedDay[panelKey] : true; // Default to true (open)
  };

  const isFormValid = () => {
    return name.trim() !== '';
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth sx={commonDialogStyles}>
        <DialogTitle>{t('routines.editRoutine')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            {/* Basic Information */}
            <TextField
              label={t('routines.routineName')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
            />
            
            <TextField
              label={t('routines.routineDescription')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder={t('routines.routineDescription')}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label={t('routines.isActive')}
            />

            {/* Days Section */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('routines.days')}</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addNewDay}
                  size="small"
                >
                  {t('routines.addDay')}
                </Button>
              </Box>

              {days.length === 0 ? (
                <Box
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <Typography color="text.secondary">
                    {t('routines.noDays')}
                  </Typography>
                </Box>
              ) : (
                days.map((day, index) => (
                  <Accordion
                    key={day.id || index}
                    expanded={isAccordionExpanded(index)}
                    onChange={handleAccordionChange(`day-${index}`)}
                    sx={{ mb: 1 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mr: 2 }}>
                        <Typography sx={{ flex: 1 }}>
                          {t('routines.dayNumber')} {index + 1}
                          {day.day_name && `: ${day.day_name}`}
                        </Typography>
                        <Chip
                          label={`${day.exercises.length} ${t('routines.exercises')}`}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            editDay(day);
                          }}
                          title={t('routines.editDay')}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteDay(day);
                          }}
                          title={t('routines.deleteDay')}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        {day.notes && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {day.notes}
                          </Typography>
                        )}
                        {day.exercises.length === 0 ? (
                          <Typography color="text.secondary">
                            {t('routines.noExercises')}
                          </Typography>
                        ) : (
                          <Box>
                            {day.exercises.map((exercise, exerciseIndex) => (
                              <Box key={exercise.id || exerciseIndex} sx={{ mb: 1 }}>
                                <Typography variant="subtitle2">
                                  {exerciseIndex + 1}. {exercise.exercise?.name || 'Unknown Exercise'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {exercise.sets.map((set, setIndex) => (
                                    `${set.sets} × ${set.reps}`
                                  )).join(', ')}
                                </Typography>
                                {exercise.notes && (
                                  <Typography variant="caption" color="text.secondary">
                                    {exercise.notes}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={!isFormValid()}
          >
            {t('common.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Day Dialog */}
      {selectedDay && (
        <EditDayDialog
          open={editDayDialogOpen}
          onClose={handleDayCancel}
          onSave={handleDaySave}
          day={selectedDay}
          exercises={exercises}
          onCreateExercise={onCreateExercise}
          setExercises={setExercises}
        />
      )}
    </>
  );
}; 